import {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";

import { getPersonalTrainerSubscription } from "~/modules/sales/services";
import type { Subscription } from "~/modules/sales/types";

import { ActiveSubscription } from "./components/ActiveSubscription";
import { LicenseExtensions } from "./components/LicenseExtensions";
import { NoSubscription } from "./components/NoSubscription";
import { PurchaseHistory } from "./components/PurchaseHistory";

type Tab = "assinatura" | "historico";

type SubscriptionsContextData = {
	subscription?: Subscription;
	isValid: boolean;
	updateSubscription: (payload: Subscription) => void;
};

export const subscriptionsContext = createContext<SubscriptionsContextData>(
	{} as SubscriptionsContextData,
);

export function Subscriptions() {
	const [subscription, setSubscription] = useState<Subscription>();
	const [loading, setLoading] = useState<boolean>(true);
	const [activeTab, setActiveTab] = useState<Tab>("assinatura");

	const updateSubscription = useCallback((payload: Subscription) => {
		setSubscription(payload);
	}, []);

	const isValid = useMemo(() => {
		if (!subscription) return false;
		const endPeriod = new Date(subscription.termEnd);
		endPeriod.setDate(endPeriod.getDate() + 1);
		return subscription.status === "active" && endPeriod > new Date();
	}, [subscription]);

	useEffect(() => {
		const abortController = new AbortController();
		getPersonalTrainerSubscription(abortController).then((data) => {
			setSubscription(data);
			setLoading(false);
		});
		return () => {
			abortController.abort();
		};
	}, []);

	return (
		<subscriptionsContext.Provider
			value={{ subscription, updateSubscription, isValid }}
		>
			<div className="flex flex-col flex-1 bg-slate-100 overflow-hidden">
				{/* Tab bar */}
				<div className="p-5 pb-0">
					<div className="flex gap-4 max-w-[860px] mx-auto">
						{(
							[
								["assinatura", "Assinatura"],
								["historico", "Histórico de Compras"],
							] as [Tab, string][]
						).map(([key, label]) => (
							<button
								key={key}
								type="button"
								onClick={() => setActiveTab(key)}
								data-selected={activeTab === key}
								className="rounded-md px-3 py-1 bg-gpa-blue-500/10
                data-[selected=true]:bg-gpa-blue-500
                data-[selected=true]:text-white
                hover:bg-opacity-30"
							>
								{label}
							</button>
						))}
					</div>
				</div>

				{/* Tab content */}
				<div className="flex-1 overflow-y-auto p-5">
					<div className="max-w-[860px] w-full mx-auto flex flex-col gap-4">
						{activeTab === "assinatura" && (
							<>
								{loading ? (
									<div className="h-10 w-10 border-[8px] rounded-full border-r-gpa-blue-50 border-gpa-blue-500 animate-spin mx-auto" />
								) : null}

								{!loading &&
								(!subscription || subscription.status === "canceled") ? (
									<NoSubscription />
								) : null}

								{loading ? null : (
									<h3 className="font-semibold">Suas assinaturas</h3>
								)}

								{subscription && !loading ? <ActiveSubscription /> : null}

								{!loading && !isValid ? (
									<div className="flex justify-between items-center">
										<h1 className="text-2xl font-bold">
											Veja as opções disponíveis
										</h1>
										<a
											href="/ofertas"
											target="_blank"
											rel="noopener noreferrer"
											className="px-3 py-2 bg-gpa-blue-500 rounded-lg font-semibold
                        text-white hover:bg-[rgb(48,137,226)] transition-colors duration-200"
										>
											Ir para ofertas
										</a>
									</div>
								) : null}

								{subscription && isValid ? (
									<>
										<div className="h-[1px] w-full bg-slate-300 my-4" />
										<LicenseExtensions
											termStart={subscription.termStart}
											termEnd={subscription.termEnd}
										/>
									</>
								) : null}
							</>
						)}

						{activeTab === "historico" && (
							<div className="p-5 pt-4 overflow-y-auto overflow-x-hidden">
								<PurchaseHistory />
							</div>
						)}
					</div>
				</div>
			</div>
		</subscriptionsContext.Provider>
	);
}
